"use client"

import { useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { Mail, ArrowRight, Check, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { toast } from "sonner"
import Link from "next/link"
import { motion, type Variants } from "motion/react"
import { requestPasswordReset, type AuthActionResult } from "@/app/actions/auth-actions"

const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
})

type FormValues = z.infer<typeof formSchema>

const fadeInVariants: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 }
}

const slideUpVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 }
}

export default function ForgotPasswordPage() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState("")
  const [isPending, startTransition] = useTransition()
  const router = useRouter()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "" },
  })

  const onSubmit = (data: FormValues) => {
    startTransition(async () => {
      try {
        const result = await requestPasswordReset(data.email)
        
        if (result.success) {
          toast.success(result.message)
          setSubmittedEmail(data.email)
          setIsSubmitted(true)
        } else {
          toast.error(result.message)
          console.error("Password reset error:", (result as Extract<typeof result, { success: false }>).error)
        }
      } catch (error) {
        console.error("Password reset request failed:", error)
        toast.error("An unexpected error occurred. Please try again.")
      }
    })
  }

  const handleResendEmail = () => {
    if (submittedEmail) {
      setIsSubmitted(false)
      form.setValue("email", submittedEmail)
    }
  }

  return (
    <main className="min-h-screen flex flex-col md:flex-row">
      <motion.div 
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5 }}
        className="w-full md:w-1/2 flex flex-col justify-center items-center p-8 md:p-16 bg-gradient-to-b from-[var(--color-neutral-50)] via-white to-[var(--color-hover-overlay)]"
      >
        <div className="w-full max-w-md space-y-8">
          <header className="text-center md:text-left space-y-3">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-[var(--color-brand-primary)] to-[var(--color-brand-primary-hover)] bg-clip-text text-transparent">
              Reset Password
            </h1>
            <p className="text-[var(--color-neutral-600)]">
              {isSubmitted 
                ? "Check your email for reset instructions" 
                : "Enter your email to receive a password reset link"}
            </p>
          </header>
          
          {!isSubmitted ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[var(--color-neutral-900)]">Email</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-neutral-500)] h-4 w-4" />
                          <Input 
                            type="email" 
                            placeholder="user@rentwise.co" 
                            className="pl-10 bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] focus:border-[var(--color-brand-primary)] transition-colors" 
                            disabled={isPending}
                            {...field} 
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit" 
                  className="w-full bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] transition-colors"
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Reset Link 
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </Form>
          ) : (
            <motion.div 
              variants={slideUpVariants}
              initial="hidden"
              animate="visible"
              className="bg-[var(--color-cta-success)]/10 border border-[var(--color-cta-success)]/20 rounded-lg p-6 text-center"
              role="alert"
              aria-live="polite"
            >
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-[var(--color-cta-success)]/20 p-2">
                  <Check className="h-6 w-6 text-[var(--color-cta-success)]" />
                </div>
              </div>
              <h3 className="text-lg font-medium text-[var(--color-neutral-900)]">Check your email</h3>
              <p className="mt-2 text-sm text-[var(--color-neutral-600)]">
                We've sent a password reset link to{" "}
                <span className="font-medium text-[var(--color-neutral-900)]">{submittedEmail}</span>.
                Please check your inbox and follow the instructions.
              </p>
              <div className="mt-4 space-y-2">
                <Button 
                  onClick={() => router.push('/login')}
                  className="w-full bg-[var(--color-cta-success)] hover:bg-[var(--color-cta-success)]/80 transition-colors"
                >
                  Return to Sign In
                </Button>
                <button
                  onClick={handleResendEmail}
                  className="w-full text-sm text-[var(--color-neutral-600)] hover:text-[var(--color-brand-primary)] transition-colors underline-offset-4 hover:underline"
                  disabled={isPending}
                >
                  Didn't receive the email? Try again
                </button>
              </div>
            </motion.div>
          )}
          
          <footer className="text-center mt-8">
            <p className="text-sm text-[var(--color-neutral-600)]">
              Remember your password?{" "}
              <Link 
                href="/login" 
                className="font-medium text-[var(--color-brand-primary)] hover:text-[var(--color-brand-primary-hover)] transition-colors underline-offset-4 hover:underline focus:underline focus:outline-none"
              >
                Sign in
              </Link>
            </p>
          </footer>
        </div>
      </motion.div>
      
      <motion.aside 
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5, delay: 0.2 }}
        className="hidden md:block w-1/2 bg-[var(--color-hover-overlay)] relative overflow-hidden"
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-brand-primary)]/10 to-[var(--color-brand-primary)]/20 opacity-80" />
        
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white/20 rounded-full animate-pulse [animation-delay:0ms]" />
          <div className="absolute top-3/4 left-1/3 w-24 h-24 bg-white/20 rounded-full animate-pulse [animation-delay:1000ms]" />
          <div className="absolute top-1/2 right-1/4 w-40 h-40 bg-white/20 rounded-full animate-pulse [animation-delay:2000ms]" />
          
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-8 shadow-xl max-w-md">
              <div className="flex flex-col space-y-4">
                <div className="h-8 w-32 bg-white/30 rounded animate-pulse" />
                <div className="h-6 w-full bg-white/30 rounded animate-pulse" />
                <div className="h-6 w-3/4 bg-white/30 rounded animate-pulse" />
                <div className="h-6 w-1/2 bg-white/30 rounded animate-pulse" />
                <div className="h-10 w-1/3 bg-white/30 rounded animate-pulse mt-2" />
              </div>
            </div>
          </div>
        </div>
      </motion.aside>
    </main>
  )
} 